import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import type { LrsCrossSection, LrsRouteKpis, LrsVerticalProfile } from './lrs.service';

export type LrsQuickAction =
  | 'add-asset'
  | 'add-utility'
  | 'add-structure'
  | 'draw-corridor'
  | 'upload-file'
  | 'take-photo'
  | 'generate-report'
  | 'export-data'
  | 'open-alignment';

export interface LrsDashboardAsset {
  kind: 'event' | 'utility';
  id: number;
  measure_m?: number;
  label?: string;
  typeId?: string;
}

@Injectable({ providedIn: 'root' })
export class LrsDashboardService {
  private readonly routeId = new BehaviorSubject<number | null>(null);
  private readonly kpis = new BehaviorSubject<LrsRouteKpis | null>(null);
  private readonly crossSection = new BehaviorSubject<LrsCrossSection | null>(null);
  private readonly selectedAsset = new BehaviorSubject<LrsDashboardAsset | null>(null);
  private readonly chainage = new BehaviorSubject<{ measure_m: number; label: string } | null>(null);
  private readonly actions = new Subject<LrsQuickAction>();
  private readonly media = new Subject<'photo' | 'file'>();
  private readonly kpisDirty = new Subject<void>();
  private readonly verticalProfile = new BehaviorSubject<LrsVerticalProfile | null>(null);
  private readonly routeMeta = new BehaviorSubject<{ origin_m: number; length_m: number } | null>(null);
  private readonly stationSeek = new Subject<{ measure_m: number; label: string }>();

  readonly routeId$ = this.routeId.asObservable();
  readonly kpis$ = this.kpis.asObservable();
  readonly crossSection$ = this.crossSection.asObservable();
  readonly selectedAsset$ = this.selectedAsset.asObservable();
  readonly chainage$ = this.chainage.asObservable();
  readonly actions$ = this.actions.asObservable();
  readonly mediaRequest$ = this.media.asObservable();
  readonly kpisDirty$ = this.kpisDirty.asObservable();
  readonly verticalProfile$ = this.verticalProfile.asObservable();
  readonly routeMeta$ = this.routeMeta.asObservable();
  readonly stationSeek$ = this.stationSeek.asObservable();

  getRouteId(): number | null {
    return this.routeId.value;
  }

  getSelectedAsset(): LrsDashboardAsset | null {
    return this.selectedAsset.value;
  }

  getChainage(): { measure_m: number; label: string } | null {
    return this.chainage.value;
  }

  setRouteId(routeId: number | null): void {
    if (this.routeId.value === routeId) return;
    this.routeId.next(routeId);
    if (routeId == null) {
      this.kpis.next(null);
      this.crossSection.next(null);
      this.selectedAsset.next(null);
      this.chainage.next(null);
      this.verticalProfile.next(null);
      this.routeMeta.next(null);
    }
  }

  setVerticalProfile(profile: LrsVerticalProfile | null): void {
    this.verticalProfile.next(profile);
    if (profile) {
      this.setRouteMeta({ origin_m: profile.origin_m, length_m: profile.length_m });
    }
  }

  getVerticalProfile(): LrsVerticalProfile | null {
    return this.verticalProfile.value;
  }

  setRouteMeta(meta: { origin_m: number; length_m: number } | null): void {
    this.routeMeta.next(meta);
  }

  getRouteMeta(): { origin_m: number; length_m: number } | null {
    return this.routeMeta.value;
  }

  setKpis(kpis: LrsRouteKpis | null): void {
    this.kpis.next(kpis);
  }

  setCrossSection(section: LrsCrossSection | null): void {
    this.crossSection.next(section);
  }

  selectAsset(asset: LrsDashboardAsset | null): void {
    this.selectedAsset.next(asset);
  }

  setChainage(hud: { measure_m: number; label: string } | null): void {
    const prev = this.chainage.value;
    if (hud && prev && Math.abs(prev.measure_m - hud.measure_m) < 1 && prev.label === hud.label) {
      return;
    }
    this.chainage.next(hud);
  }

  seekStation(hud: { measure_m: number; label: string }): void {
    this.setChainage(hud);
    this.stationSeek.next(hud);
  }

  requestAction(action: LrsQuickAction): void {
    this.actions.next(action);
  }

  requestMedia(kind: 'photo' | 'file'): void {
    this.media.next(kind);
  }

  markDirty(): void {
    this.kpisDirty.next();
  }
}
